/**
 * "Kies uit CWE-bibliotheek" — zoek-en-kopieer picker voor «Vulnerability»-
 * concepten. Twee stappen, beide zoek-en-kopieer (geen gedeeld object,
 * geen live-referentie — CLAUDE.md 2026-07-24 "zoek-en-kopieer i.p.v.
 * materialiseren"):
 *
 * 1. Zoekt live tegen de master-workspace's MITRE CWE-bibliotheek
 *    (`GET /api/v1/mitre-cwe/search`) — kopieert naam/beschrijving/CWE-
 *    metadata bij selectie.
 * 2. Meteen daarna: live tegen NVD gezocht op CVE's die deze CWE als
 *    weakness-type hebben (`GET /api/v1/mitre-cwe/cve-search`) — CWE zelf
 *    heeft geen CVSS/exploitability/datums (dat zijn instantie-attrs van
 *    een concrete CVE, niet van de abstracte weakness-taxonomie), dus dat
 *    haalt de picker er automatisch bij zodat je niet apart hoeft te
 *    zoeken. Overslaan kan altijd — een CWE zonder gekoppelde CVE is een
 *    prima eindstaat.
 */
import type { Concept } from "@ae/domain";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type CveSearchResultDTO, type CweSearchResultDTO } from "../../api/client";
import { resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

type Stage = "cwe" | "cve";

/** "Path traversal [Common-Name, Attack-Focused]; Directory traversal" →
 *  "Path traversal · Directory traversal": de rollen zijn voor de lijst ruis. */
function alternateTermNames(stored: string): string {
  return stored
    .split("; ")
    .map((t) => t.replace(/ \[[^\]]*\]$/, ""))
    .join(" · ");
}

export function CweLibraryPicker({
  concept,
  workspaceId,
  branchId,
  lang,
}: {
  concept: Concept;
  workspaceId: string;
  branchId: string;
  lang: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("cwe");
  const [query, setQuery] = useState("");
  const [cweResults, setCweResults] = useState<CweSearchResultDTO[]>([]);
  const [cveResults, setCveResults] = useState<CveSearchResultDTO[]>([]);
  const [pickedCweId, setPickedCweId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setOpen(false);
    setStage("cwe");
    setQuery("");
    setCweResults([]);
    setCveResults([]);
    setPickedCweId(null);
  };

  useEffect(() => {
    if (open && stage === "cwe") inputRef.current?.focus();
  }, [open, stage]);

  // Stap 1 — CWE-zoek (debounced op typen).
  useEffect(() => {
    if (!open || stage !== "cwe") return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setCweResults([]);
      return;
    }
    setBusy(true);
    timerRef.current = setTimeout(() => {
      void api
        .searchMitreCwe(query.trim())
        .then((res) => {
          setConfigured(res.configured);
          setCweResults(res.results);
        })
        .catch(() => setCweResults([]))
        .finally(() => setBusy(false));
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, open, stage]);

  // Stap 2 — CVE-zoek voor de gekozen CWE, direct getriggerd (geen typen nodig).
  useEffect(() => {
    if (stage !== "cve" || !pickedCweId) return;
    setBusy(true);
    void api
      .searchNvdCve(pickedCweId)
      .then((res) => setCveResults(res.results))
      .catch(() => setCveResults([]))
      .finally(() => setBusy(false));
  }, [stage, pickedCweId]);

  const applyCwe = (r: CweSearchResultDTO) => {
    void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
      name: { ...concept.name, ...r.name },
      description: { ...concept.description, ...r.description },
      attributes: {
        ...concept.attributes,
        cwe_id: r.cweId,
        cwe_abstraction: r.abstraction,
        cwe_structure: r.structure,
        cwe_status: r.status,
        cwe_impacts: r.impacts,
        cwe_likelihood_of_exploit: r.likelihoodOfExploit,
        related_cwes: r.relatedCwes,
        cwe_references: r.references,
        cwe_alternate_terms: r.alternateTerms,
      },
    });
    setQuery("");
    setCweResults([]);
    setPickedCweId(r.cweId);
    setStage("cve");
  };

  const applyCve = (r: CveSearchResultDTO) => {
    // Fris de concept-attrs op i.p.v. de (mogelijk stale) prop uit deze
    // render — de CWE-apply hierboven kan intussen al door de store zijn
    // verwerkt zonder dat deze component opnieuw rendert met de nieuwe prop.
    const current = useWorkspaceData.getState().store.getConcept(concept.id) ?? concept;
    void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
      attributes: {
        ...current.attributes,
        cve_id: r.cveId,
        ...(r.cvssScore !== null ? { cvss_score: String(r.cvssScore) } : {}),
        ...(r.cvssVector !== null ? { cvss_vector: r.cvssVector } : {}),
        ...(r.cvssSeverity !== null ? { cvss_severity: r.cvssSeverity } : {}),
        ...(r.published !== null
          ? { discovery_date: r.published, disclosure_date: r.published }
          : {}),
      },
    });
    reset();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
      >
        {t("attributePanel.cweLibrary.openButton")}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 rounded border border-(--ld-border) bg-(--ld-raised) p-2">
      {stage === "cwe" && (
        <>
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("attributePanel.cweLibrary.searchPlaceholder")}
              className="flex-1 rounded border border-(--ld-muted) px-2 py-1 text-xs"
            />
            <button
              type="button"
              onClick={reset}
              className="rounded px-1.5 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised) hover:text-(--ld-text)"
            >
              {t("attributePanel.cweLibrary.closeButton")}
            </button>
          </div>
          {!configured && (
            <p className="text-(--ld-notice-ink) text-xs">
              {t("attributePanel.cweLibrary.notConfigured")}
            </p>
          )}
          {busy && <p className="text-(--ld-muted) text-xs">{t("settings.common.loading")}</p>}
          {!busy &&
            query.trim().length >= MIN_QUERY_LENGTH &&
            cweResults.length === 0 &&
            configured && (
              <p className="text-(--ld-muted) text-xs">
                {t("attributePanel.cweLibrary.noResults")}
              </p>
            )}
          {cweResults.length > 0 && (
            <ul className="flex max-h-48 flex-col gap-0.5 overflow-y-auto">
              {cweResults.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => applyCwe(r)}
                    className="w-full rounded px-2 py-1 text-left text-xs hover:bg-(--ld-active)"
                    title={resolveBilingual(r.description, lang)}
                  >
                    <span className="font-mono text-(--ld-muted)">{r.cweId}</span>{" "}
                    <span className="text-(--ld-text)">{resolveBilingual(r.name, lang)}</span>
                    {r.alternateTerms && (
                      <span className="block text-(--ld-muted)">
                        {alternateTermNames(r.alternateTerms)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {stage === "cve" && (
        <>
          <div className="flex items-center justify-between gap-1.5">
            <p className="text-(--ld-muted) text-xs">
              {t("attributePanel.cweLibrary.cveHeading", { cweId: pickedCweId })}
            </p>
            <button
              type="button"
              onClick={reset}
              className="rounded px-1.5 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised) hover:text-(--ld-text)"
            >
              {t("attributePanel.cweLibrary.skipButton")}
            </button>
          </div>
          {busy && <p className="text-(--ld-muted) text-xs">{t("settings.common.loading")}</p>}
          {!busy && cveResults.length === 0 && (
            <p className="text-(--ld-muted) text-xs">
              {t("attributePanel.cweLibrary.noCveResults")}
            </p>
          )}
          {cveResults.length > 0 && (
            <ul className="flex max-h-48 flex-col gap-0.5 overflow-y-auto">
              {cveResults.map((r) => (
                <li key={r.cveId}>
                  <button
                    type="button"
                    onClick={() => applyCve(r)}
                    className="w-full rounded px-2 py-1 text-left text-xs hover:bg-(--ld-active)"
                    title={r.description}
                  >
                    <span className="font-mono text-(--ld-muted)">{r.cveId}</span>{" "}
                    {r.cvssSeverity && (
                      <span className="rounded bg-(--ld-negative-bg) px-1 text-xs text-(--ld-negative)">
                        {r.cvssSeverity}
                        {r.cvssScore !== null ? ` ${r.cvssScore}` : ""}
                      </span>
                    )}{" "}
                    <span className="text-(--ld-text)">{r.description.slice(0, 80)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
